import { useSignal } from '@preact/signals-react';
import ATable, { ATableHeader } from '../../organisms/ATable';
import { useNavigate, Link } from 'react-router-dom';
import PageForm from '../../templates/PageForm';
import Tab from '../../molecules/Tab';
import useFetch from '../../../hooks/useFetch';
import ToggleSwitch from '../../atoms/ToogleSwitch';
import { FaDatabase, FaLayerGroup, FaCalendarAlt, FaCube, FaPlus } from 'react-icons/fa';
import { HiOutlineExternalLink, HiOutlineTrash } from 'react-icons/hi';
import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import appConfig from '../../../config/app-config';

const DEFAULT_ACCOUNT_ID = '111257797516';

const buildLayerLink = (name: string, region: string, account: string) =>
    `https://console.aws.amazon.com/lambda/home?region=${region}#/layers/${name}?accountId=${account}`;
const buildTableLink = (name: string, region: string, account: string) =>
    `https://console.aws.amazon.com/dynamodb/home?region=${region}#tables:selected=${name};tab=overview?accountId=${account}`;
const buildScheduleLink = (name: string, region: string, account: string) =>
    `https://console.aws.amazon.com/events/home?region=${region}#/schedules/${name}?accountId=${account}`;
const buildBucketLink = (name: string, region: string, account: string) =>
    `https://s3.console.aws.amazon.com/s3/buckets/${name}?region=${region}&bucketType=general&accountId=${account}`;

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const CONFIGURATION_URL = `${COMMUNICATION_ENV.address}/config/page-view`;
const API_KEY = COMMUNICATION_ENV.key;

const CommunicationConfiguration: React.FC = () => {
    const navigate = useNavigate();
    const tabIndex = useSignal(0);
    const mainTabIndex = useSignal(0);
    const [schedules, setSchedules] = useState<any[]>([]);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    const { isLoading, data, error, isTokenError } = useFetch(CONFIGURATION_URL, [
        { key: 'x-api-key', value: API_KEY },
    ]);

    // Sincronizar schedules com data
    useEffect(() => {
        if (data?.schedules) {
            setSchedules(data.schedules);
        }
    }, [data]);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = () => setOpenDropdown(null);
        if (openDropdown) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [openDropdown]);

    const region = data?.region || 'us-east-1';
    const accountId = data?.accountId || DEFAULT_ACCOUNT_ID;
    const allTemplates = data?.templates || [];

    const getAvailableTemplates = (scheduleName: string) => {
        const schedule = schedules.find(s => s.name === scheduleName);
        const usedTemplateNames = schedule?.templates?.map((t: any) => t.name) || [];
        
        return allTemplates.filter((template: any) => {
            if (!template.active) return false;
            if (usedTemplateNames.includes(template.name)) return false;
            
            if (scheduleName === 'Communication-Batch-WppStatus-Schedule') {
                return template.type === 'WPP' || template.name.startsWith('WPP_');
            }
            
            return true;
        });
    };

    const addTemplateToSchedule = async (scheduleName: string, templateName: string) => {
        const scheduleIndex = schedules.findIndex(s => s.name === scheduleName);
        if (scheduleIndex === -1) return;

        const newTemplate = {
            name: templateName,
            status: 'PENDING',
            description: allTemplates.find((t: any) => t.name === templateName)?.description || ''
        };

        const updatedSchedules = [...schedules];
        updatedSchedules[scheduleIndex] = {
            ...updatedSchedules[scheduleIndex],
            templates: [...(updatedSchedules[scheduleIndex].templates || []), newTemplate]
        };
        setSchedules(updatedSchedules);
        setOpenDropdown(null);

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(
                `${COMMUNICATION_ENV.address}/config/schedule/${scheduleName}/template`,
                {
                    method: 'POST',
                    headers: {
                        'x-api-key': API_KEY,
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ templateName })
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erro ao adicionar template');
            }

            toast.success('Template adicionado com sucesso');
        } catch (error: any) {
            updatedSchedules[scheduleIndex] = {
                ...updatedSchedules[scheduleIndex],
                templates: updatedSchedules[scheduleIndex].templates.filter((t: any) => t.name !== templateName)
            };
            setSchedules(updatedSchedules);
            toast.error(error.message || 'Erro ao adicionar template');
        }
    };

    const removeTemplateFromSchedule = async (scheduleName: string, templateName: string) => {
        if (!window.confirm(`Deseja realmente remover o template ${templateName}?`)) {
            return;
        }

        const scheduleIndex = schedules.findIndex(s => s.name === scheduleName);
        if (scheduleIndex === -1) return;

        const updatedSchedules = [...schedules];
        const originalTemplates = [...updatedSchedules[scheduleIndex].templates];
        
        updatedSchedules[scheduleIndex] = {
            ...updatedSchedules[scheduleIndex],
            templates: updatedSchedules[scheduleIndex].templates.filter((t: any) => t.name !== templateName)
        };
        setSchedules(updatedSchedules);

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(
                `${COMMUNICATION_ENV.address}/config/schedule/${scheduleName}/template/${templateName}`,
                {
                    method: 'DELETE',
                    headers: {
                        'x-api-key': API_KEY,
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erro ao remover template');
            }

            toast.success('Template removido com sucesso');
        } catch (error: any) {
            updatedSchedules[scheduleIndex] = {
                ...updatedSchedules[scheduleIndex],
                templates: originalTemplates
            };
            setSchedules(updatedSchedules);
            toast.error(error.message || 'Erro ao remover template');
        }
    };

    if (isTokenError) {
        navigate('/denied');
    }

    if (error) {
        navigate('/network-error');
    }

    const renderListTable = (items: any[]) => (
        <ATable headers={[]} autoResize={false} marginTop={0}>
            {items.map((item, index) => (
                <tr key={index} className={`${index % 2 === 0 ? 'bg-slate-100' : ''} border-b border-gray-300`}>
                    <td className="py-2 px-2 whitespace-nowrap">{String(item)}</td>
                </tr>
            ))}
        </ATable>
    );

    const renderKeyValueTable = (obj: Record<string, unknown>) => {
        const headers: ATableHeader[] = [
            { id: 'key', title: 'Chave', canSort: false, width: '200px' },
            { id: 'value', title: 'Valor', canSort: false },
        ];
        return (
            <ATable headers={headers} autoResize={false} marginTop={0}>
                {Object.entries(obj).map(([key, value], index) => (
                    <tr key={key} className={`${index % 2 === 0 ? 'bg-slate-100' : ''} border-b border-gray-300`}>
                        <td className="py-2 px-2 whitespace-nowrap">{key}</td>
                        <td className="py-2 px-2">{String(value)}</td>
                    </tr>
                ))}
            </ATable>
        );
    };

    const renderAdapter = () => {
        if (!data) return null;
        const adapters: Record<string, any> = {};
        data.adapters?.forEach((adapter: any) => {
            adapters[adapter.type.toUpperCase()] = adapter.data;
        });
        if (tabIndex.value === 0) {
            const ses = adapters.SES || {};
            const keys = Object.keys(ses).filter((k) => Array.isArray(ses[k]));
            const [allowKey, blockKey] = keys;
            return (
                <div className="grid grid-cols-2 gap-4 h-full">
                    <div className="bg-white border rounded-lg p-6 shadow overflow-y-auto">
                        <h4 className="font-semibold mb-2">{allowKey}</h4>
                        {renderListTable(ses[allowKey] || [])}
                    </div>
                    <div className="bg-white border rounded-lg p-6 shadow overflow-y-auto">
                        <h4 className="font-semibold mb-2">{blockKey}</h4>
                        {renderListTable(ses[blockKey] || [])}
                    </div>
                </div>
            );
        }
        if (tabIndex.value === 1) return renderKeyValueTable(adapters.BLIP || {});
        return renderKeyValueTable(adapters.SLACK || {});
    };

    const renderParams = () => {
        if (!data) return null;

        const notificationTable = data.params.notification_table_name || '';
        const messageTable = data.params.message_table_name || '';
        const miscTable = data.params.misc_table_name || '';
        const buckets = data.params.buckets_name || [];

        return (
            <div className="space-y-6">
                {/* Tabelas DynamoDB */}
                <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                        <FaDatabase className="text-blue-600" />
                        Tabelas DynamoDB
                    </h4>
                    <div className="space-y-2 pl-6">
                        <div className="group">
                            <label className="block text-xs text-gray-500 mb-1">Notificações</label>
                            <a
                                href={buildTableLink(notificationTable, region, accountId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors"
                            >
                                {notificationTable}
                                <HiOutlineExternalLink className="opacity-0 group-hover:opacity-100 transition-opacity" />
                            </a>
                        </div>

                        <div className="group">
                            <label className="block text-xs text-gray-500 mb-1">Mensagens</label>
                            <a
                                href={buildTableLink(messageTable, region, accountId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors"
                            >
                                {messageTable}
                                <HiOutlineExternalLink className="opacity-0 group-hover:opacity-100 transition-opacity" />
                            </a>
                        </div>

                        <div className="group">
                            <label className="block text-xs text-gray-500 mb-1">Agendamento e Totais</label>
                            <a
                                href={buildTableLink(miscTable, region, accountId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors"
                            >
                                {miscTable}
                                <HiOutlineExternalLink className="opacity-0 group-hover:opacity-100 transition-opacity" />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Buckets S3 */}
                {buckets.length > 0 && (
                    <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                            <FaCube className="text-orange-600" />
                            Buckets S3
                        </h4>
                        <div className="pl-6 space-y-1">
                            {buckets.map((bucket: string) => (
                                <div key={bucket} className="group">
                                    <a
                                        href={buildBucketLink(bucket, region, accountId)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors"
                                    >
                                        • {bucket}
                                        <HiOutlineExternalLink className="opacity-0 group-hover:opacity-100 transition-opacity text-xs" />
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };


    const renderLayers = () => {
        if (!data) return null;
        return (
            <div className="space-y-4">
                {/* Lambdas em lista vertical */}
                <div>
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <FaLayerGroup className="text-purple-600" />
                        Lambdas
                    </h4>
                    <div className="space-y-2">
                        {data.layers?.map((layer: any) => (
                            <details key={layer.name} className="border border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
                                <summary className="cursor-pointer px-3 py-2 flex justify-between items-center hover:bg-gray-50 rounded-lg transition-colors">
                                    <a
                                        href={buildLayerLink(layer.name, region, accountId)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-medium text-blue-600 hover:text-blue-800 flex items-center gap-2"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <FaLayerGroup className="text-sm" />
                                        {layer.name}
                                        <HiOutlineExternalLink className="text-xs" />
                                    </a>
                                </summary>
                                <div className="p-3 border-t bg-gray-50 overflow-x-auto max-h-48 overflow-y-auto">
                                    {Array.isArray(layer.env_vars) && layer.env_vars.length > 0
                                        ? <div className="text-xs space-y-1">
                                            {layer.env_vars.map((env: any, idx: number) => (
                                                <div key={idx} className="flex gap-2">
                                                    <span className="font-mono text-gray-600">{env.key}:</span>
                                                    <span className="font-mono text-gray-800 break-all">{env.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                        : <p className="text-xs text-gray-500 italic">Nenhuma variável configurada</p>}
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderSchedules = () => {
        if (!schedules || schedules.length === 0) return null;
        return (
            <div className="grid grid-cols-2 gap-4 h-full">
                {schedules.map((schedule: any) => (
                    <div key={schedule.name} className="border border-gray-200 rounded-lg p-4 flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                            <a
                                href={buildScheduleLink(schedule.name, region, accountId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-base font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-2"
                            >
                                {schedule.name}
                                <HiOutlineExternalLink />
                            </a>
                            <div className="flex items-center gap-2">
                                <ToggleSwitch
                                    checked={schedule.state === 'ENABLED'}
                                    onChange={() => { }}
                                    disabled
                                    activeClass="bg-green-500"
                                    inactiveClass="bg-gray-300"
                                />
                                <div className="relative">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenDropdown(openDropdown === schedule.name ? null : schedule.name);
                                        }}
                                        className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                        title="Adicionar template"
                                    >
                                        <FaPlus size={14} />
                                    </button>
                                    {openDropdown === schedule.name && (
                                        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-10 max-h-96 overflow-y-auto">
                                            {getAvailableTemplates(schedule.name).length === 0 ? (
                                                <div className="p-3 text-sm text-gray-500">Nenhum template disponível</div>
                                            ) : (
                                                getAvailableTemplates(schedule.name).map((template: any) => (
                                                    <button
                                                        key={template.name}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            addTemplateToSchedule(schedule.name, template.name);
                                                        }}
                                                        className="w-full text-left p-3 hover:bg-blue-600 hover:text-white border-b border-gray-100 last:border-b-0 transition-colors"
                                                    >
                                                        <div className="text-xs font-medium break-words">{template.name}</div>
                                                        {template.description && (
                                                            <div className="text-xs mt-1 break-words opacity-80">{template.description}</div>
                                                        )}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {schedule.templates && schedule.templates.length > 0 && (
                            <div className="flex-1 overflow-y-auto">
                                {schedule.name === 'Communication-Batch-Schedule' ?
                                    <p className="text-sm text-gray-600 font-medium mb-3">Templates configurados para disparar todos os dias as 8h:</p>
                                    :
                                    <p className="text-sm text-gray-600 font-medium mb-3">Templates configurados para disparar todos os dias as 8h, 13h e 17h:</p>
                                }


                                <div className="space-y-2">
                                    {schedule.templates.map((template: any) => (
                                        <div
                                            key={template.name}
                                            className="flex items-center gap-2"
                                        >
                                            <Link
                                                to={`/communication/template/${template.name}`}
                                                className="flex-1 block p-3 rounded-lg bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 transition-all"
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-semibold text-blue-700 mb-1">{template.name}</div>
                                                        {template.description && (
                                                            <div className="text-xs text-gray-600">{template.description}</div>
                                                        )}
                                                    </div>
                                                    {template.status && (
                                                        <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${template.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                                            template.status === 'SENT' ? 'bg-green-100 text-green-800' :
                                                                'bg-gray-100 text-gray-800'
                                                            }`}>
                                                            {template.status}
                                                        </span>
                                                    )}
                                                </div>
                                            </Link>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeTemplateFromSchedule(schedule.name, template.name);
                                                }}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Remover template"
                                            >
                                                <HiOutlineTrash size={18} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <PageForm
            pageTitle="Configuração - Comunicação"
            apiVersion={data ? `${data.apiName} - ${data.apiVersion}` : ''}
            isLoading={isLoading}
            breadcrumb={[{ label: 'Configurações - comunicação', url: '#' }]}
            controlSlot={<></>}
        >
            <div className="container mx-auto py-4 h-full flex flex-col gap-4">
                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12 md:col-span-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <div className="w-1 h-6 bg-blue-600 rounded-full"></div>
                            Configurações Gerais
                        </h3>
                        {renderParams()}
                    </div>
                    <div className="col-span-12 md:col-span-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <div className="w-1 h-6 bg-purple-600 rounded-full"></div>
                            Camadas da Aplicação
                        </h3>
                        {renderLayers()}
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow flex-1 flex flex-col min-h-0">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <div className="w-1 h-6 bg-indigo-600 rounded-full"></div>
                        Serviços e Agendamentos
                    </h3>
                    <Tab
                        tabItems={[
                            { id: 'schedules', title: 'Agendamentos', index: 0, icon: FaCalendarAlt },
                            { id: 'adapters', title: 'Adaptadores', index: 1 },
                        ]}
                        tabIndex={mainTabIndex.value}
                        setTabIndex={(index) => (mainTabIndex.value = index)}
                    />
                    <div className="mt-4 flex-1 overflow-auto">
                        {mainTabIndex.value === 0 && renderSchedules()}
                        {mainTabIndex.value === 1 && (
                            <>
                                <Tab
                                    tabItems={[
                                        { id: 'ses', title: 'SES', index: 0 },
                                        { id: 'blip', title: 'BLIP', index: 1 },
                                        { id: 'slack', title: 'Slack', index: 2 },
                                    ]}
                                    tabIndex={tabIndex.value}
                                    setTabIndex={(index) => (tabIndex.value = index)}
                                />
                                <div className="mt-4">{renderAdapter()}</div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </PageForm>
    );
};

export default CommunicationConfiguration;
